import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  MODULE_PRICES_QUERY_KEY,
  getModulePrices,
  setModulePrice,
  type ModulePrice,
} from '@/features/tenants/api/subscription.graphql';
import { formatLifecycleDate } from '@/features/tenants/tenant-labels';
import { formatMoney } from '@/lib/money';

import styles from './PricesPage.module.scss';

type Editing = { module: ModulePrice; remove: boolean };

/** Precio mensual escrito por el usuario: positivo y con máximo 2 decimales (`1.234,5` no; `1234,5` sí). */
export function parseAmount(text: string): number | null {
  const normalized = text.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    return null;
  }
  const value = Number(normalized);
  return value > 0 ? value : null;
}

/**
 * OS-1723 — lista de precios por módulo (mensual, COP). La lee quien tiene `platform.billing.read`;
 * solo SUPER la cambia (`canEdit` lo decide el API). Cambiar un precio no toca las suscripciones
 * existentes: aplica a las líneas que se agreguen después.
 */
export function PricesPage(): JSX.Element {
  const qc = useQueryClient();
  const idPrefix = useId();
  const query = useQuery({ queryKey: MODULE_PRICES_QUERY_KEY, queryFn: getModulePrices });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [amountText, setAmountText] = useState('');
  const [notice, setNotice] = useState('');

  const mutation = useMutation({
    mutationFn: ({ module, amount }: { module: ModulePrice; amount: number | null }) =>
      setModulePrice(module.moduleKey, amount),
    onSuccess: (list, { module, amount }) => {
      qc.setQueryData(MODULE_PRICES_QUERY_KEY, list);
      // Las fichas muestran el precio de lista vigente junto al contratado.
      void qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      setNotice(
        amount === null
          ? `${module.name} quedó sin precio de lista.`
          : `${module.name}: ${formatMoney(amount)} al mes. El cambio quedó auditado.`,
      );
      setEditing(null);
    },
  });

  function open(module: ModulePrice, remove: boolean): void {
    mutation.reset();
    setNotice('');
    setAmountText(module.amountMonthly === null ? '' : String(module.amountMonthly));
    setEditing({ module, remove });
  }

  const amount = parseAmount(amountText);
  const canEdit = query.data?.canEdit ?? false;

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Precios por módulo</h1>
        <p className={styles.muted}>
          Precio mensual de lista en {query.data?.currency ?? 'COP'}. Al agregar un módulo a la
          suscripción de un tenant se copia este precio; cambiarlo después no modifica contratos
          existentes. Un módulo sin precio no se puede agregar a una suscripción.
        </p>
        {query.data && !canEdit ? (
          <p className={styles.muted}>Solo lectura: la lista de precios la cambia un Super.</p>
        ) : null}
      </div>

      <p className={styles.notice} role="status" aria-live="polite">
        {notice}
      </p>

      {query.isLoading ? <p className={styles.muted}>Cargando precios…</p> : null}
      {query.isError ? (
        <p className={styles.error} role="alert">
          No se pudo cargar la lista de precios: {query.error.message}
        </p>
      ) : null}

      {query.data ? (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <caption className={styles.srOnly}>Precios mensuales por módulo</caption>
            <thead>
              <tr>
                <th scope="col">Módulo</th>
                <th scope="col">Precio mensual</th>
                <th scope="col">Vigente desde</th>
                {canEdit ? <th scope="col">Acciones</th> : null}
              </tr>
            </thead>
            <tbody>
              {query.data.modules.map((module) => (
                <tr key={module.moduleKey}>
                  <th scope="row">
                    {module.name}
                    {module.isCore ? <span className={styles.badge}>base</span> : null}
                  </th>
                  <td>
                    {module.amountMonthly === null
                      ? 'Sin precio'
                      : formatMoney(module.amountMonthly, module.currency)}
                  </td>
                  <td>{module.validFrom ? formatLifecycleDate(module.validFrom) : '—'}</td>
                  {canEdit ? (
                    <td>
                      <div className={styles.actions}>
                        <button
                          type="button"
                          className={styles.linkBtn}
                          onClick={() => open(module, false)}
                          aria-label={`${module.amountMonthly === null ? 'Definir' : 'Cambiar'} precio de ${module.name}`}
                        >
                          {module.amountMonthly === null ? 'Definir precio' : 'Cambiar'}
                        </button>
                        {module.amountMonthly !== null ? (
                          <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => open(module, true)}
                            aria-label={`Quitar precio de ${module.name}`}
                          >
                            Quitar precio
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {editing && !editing.remove ? (
        <ConfirmDialog
          title={`Precio de ${editing.module.name}`}
          confirmLabel="Guardar precio"
          pending={mutation.isPending}
          confirmDisabled={amount === null}
          error={mutation.error?.message ?? null}
          onConfirm={() => {
            if (amount !== null) {mutation.mutate({ module: editing.module, amount });}
          }}
          onCancel={() => setEditing(null)}
        >
          <label className={styles.field}>
            Precio mensual (COP)
            <input
              type="text"
              inputMode="decimal"
              value={amountText}
              onChange={(event) => setAmountText(event.target.value)}
              aria-invalid={amount === null}
              aria-describedby={`${idPrefix}-amount-hint`}
              placeholder="Ej.: 250000"
            />
          </label>
          <p id={`${idPrefix}-amount-hint`} className={styles.muted}>
            {amount === null
              ? 'Escribe un valor mayor que 0, sin puntos de miles y con máximo 2 decimales.'
              : `Quedará en ${formatMoney(amount)} al mes desde ahora. Las suscripciones existentes no cambian.`}
          </p>
        </ConfirmDialog>
      ) : null}

      {editing?.remove ? (
        <ConfirmDialog
          title={`Quitar el precio de ${editing.module.name}`}
          confirmLabel="Quitar precio"
          danger
          pending={mutation.isPending}
          error={mutation.error?.message ?? null}
          onConfirm={() => mutation.mutate({ module: editing.module, amount: null })}
          onCancel={() => setEditing(null)}
        >
          <p>
            El módulo no se podrá agregar a nuevas suscripciones hasta definir otro precio. Las
            suscripciones que ya lo cobran no cambian.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
